import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import BusinessMpProvider from "@/core/data-provider/data-mp/business/business-mp-provider";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import { setPricesForServicesList, updatePricesForServiceList } from "@/domains/business/slice";
import toast from "@/domains/toasts/helpers/toast";

export default function useGetPriceForServicesData(businessId: number) {
  const { t, i18n } = useTranslation("business");
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isFirstLoading, setIsFirstLoading] = useState(true);

  const load = useCallback(async () => {
    if (businessId) {
      setIsLoading(true);

      try {
        const response = await BusinessMpProvider.getServiceProductsList(businessId);

        if (response instanceof RpcMpProviderSuccessResponse) {
          dispatch(setPricesForServicesList(response.data));
          setIsFirstLoading(false);
        } else if (i18n.exists(`business:business.errors.${response.error.message}`)) {
          console.error(response.error.message);
          toast.error(t(`business.errors.${response.error.message}`));
        } else {
          console.error(response.error.message);
          toast.error(t("business.toast.pricesForServicesNotLoaded"));
        }
      } catch (e) {
        console.error(e);
        toast.error(t("business.toast.pricesForServicesNotLoaded"));
      } finally {
        setIsLoading(false);
      }
    }
  }, [businessId, i18n, dispatch, t]);

  useEffect(() => {
    load();
  }, [load]);

  const setExtraCharge = useCallback(
    async (productId: number, percent: number, setIsAccepted: (arg0: boolean) => void) => {
      if (businessId && productId && typeof percent === "number") {
        setIsUpdating(true);

        try {
          const response = await BusinessMpProvider.setServiceProductExtraCharge(
            businessId,
            productId,
            percent,
          );

          if (response instanceof RpcMpProviderSuccessResponse) {
            setIsAccepted(true);
            dispatch(
              updatePricesForServiceList({
                productId,
                percent: response.data.percent,
                pricedAt: response.data.pricedAt,
              }),
            );
          } else if (i18n.exists(`business:business.errors.${response.error.message}`)) {
            console.error(response.error.message);
            toast.error(t(`business.errors.${response.error.message}`));
          } else {
            console.error(response.error.message);
            toast.error(t("business.toast.pricesForServicesNotUpdate"));
          }
        } catch (e) {
          console.error(e);
          toast.error(t("business.toast.pricesForServicesNotUpdate"));
        } finally {
          setIsUpdating(false);
        }
      }
    },
    [businessId, dispatch, i18n, t],
  );

  return {
    isLoading,
    setExtraCharge,
    isUpdating,
    isFirstLoading,
  };
}
